import type { AppEntry } from "../registry/apps";
import { isLive } from "./progress";

/**
 * Default wall order.
 * Featured puts curated apps first, then everyone else in 1–n ship order
 * (`shippedAt`, then `slug`, then `surface`). The home frame opens on a
 * showcase instead of a raw chronological dump.
 *
 * Per-session override: sessionStorage `over9000.wall.order`
 * (`featured` | `new` | `numeric` | `random`). Every first visit opens on Featured.
 * Random shuffles persist their seed in `over9000.wall.shuffleSeed`.
 * Clicking RANDOM while it is already selected, or RESHUFFLE, picks a new seed.
 */
export const DEFAULT_WALL_ORDER = "featured" as const;

export const WALL_ORDER_KEY = "over9000.wall.order";
export const WALL_SHUFFLE_KEY = "over9000.wall.shuffleSeed";
export const DEFAULT_SHUFFLE_SEED = 9001;

export type WallOrder = "random" | "numeric" | "featured" | "new";

export type KeyValueStore = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

export function isWallOrder(value: string | null | undefined): value is WallOrder {
  return value === "random" || value === "numeric" || value === "featured" || value === "new";
}

/** Live, non-example ships. Example, build, wip, and `upstreamStaging` stay off the wall. */
export function appsForWall(apps: readonly AppEntry[]): AppEntry[] {
  return apps.filter(isLive);
}

export function isFeatured(app: Pick<AppEntry, "featured" | "featuredOrder">): boolean {
  return app.featured === true || typeof app.featuredOrder === "number";
}

/** Stable 1–n order: ship day, then slug, then surface. */
export function compareShipOrder(a: AppEntry, b: AppEntry): number {
  const byDay = a.shippedAt.localeCompare(b.shippedAt);
  if (byDay !== 0) return byDay;
  const bySlug = a.slug.localeCompare(b.slug);
  if (bySlug !== 0) return bySlug;
  return a.surface.localeCompare(b.surface);
}

function featuredRank(app: AppEntry): number | null {
  if (typeof app.featuredOrder === "number" && Number.isFinite(app.featuredOrder)) {
    return app.featuredOrder;
  }
  if (app.featured === true) return Number.POSITIVE_INFINITY;
  return null;
}

/** Featured group first (by featuredOrder), then 1–n. */
export function compareFeatured(a: AppEntry, b: AppEntry): number {
  const ar = featuredRank(a);
  const br = featuredRank(b);
  const aFeat = ar !== null;
  const bFeat = br !== null;
  if (aFeat !== bFeat) return aFeat ? -1 : 1;
  if (aFeat && bFeat && ar !== br) return (ar ?? 0) - (br ?? 0);
  return compareShipOrder(a, b);
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic Fisher–Yates. Does not mutate `apps`. */
export function shuffleApps(apps: readonly AppEntry[], seed: number): AppEntry[] {
  const out = apps.slice();
  const rand = mulberry32(seed || DEFAULT_SHUFFLE_SEED);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const cur = out[i];
    const swap = out[j];
    if (cur && swap) {
      out[i] = swap;
      out[j] = cur;
    }
  }
  return out;
}

export function orderApps(
  apps: readonly AppEntry[],
  mode: WallOrder,
  seed = DEFAULT_SHUFFLE_SEED,
): AppEntry[] {
  if (mode === "random") return shuffleApps(apps, seed);
  if (mode === "new") {
    // Newest ship day first; same day → later registry entry first.
    return apps
      .map((app, i) => ({ app, i }))
      .sort((a, b) => b.app.shippedAt.localeCompare(a.app.shippedAt) || b.i - a.i)
      .map(({ app }) => app);
  }
  const compare = mode === "featured" ? compareFeatured : compareShipOrder;
  return apps.slice().sort(compare);
}

export function readWallOrder(store: KeyValueStore | null): WallOrder {
  const raw = store?.getItem(WALL_ORDER_KEY);
  return isWallOrder(raw) ? raw : DEFAULT_WALL_ORDER;
}

export function writeWallOrder(store: KeyValueStore | null, order: WallOrder): void {
  store?.setItem(WALL_ORDER_KEY, order);
}

export function readShuffleSeed(store: KeyValueStore | null): number {
  const raw = store?.getItem(WALL_SHUFFLE_KEY);
  if (!raw) return DEFAULT_SHUFFLE_SEED;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return DEFAULT_SHUFFLE_SEED;
  return n >>> 0;
}

export function writeShuffleSeed(store: KeyValueStore | null, seed: number): void {
  const n = seed >>> 0;
  store?.setItem(WALL_SHUFFLE_KEY, String(n === 0 ? DEFAULT_SHUFFLE_SEED : n));
}

/** Next persisted shuffle seed. Deterministic so a click always changes the wall. */
export function nextShuffleSeed(prev: number): number {
  const n = (Math.imul((prev ^ 0x9e3779b9) >>> 0, 0x6c078965) + 1) >>> 0;
  return n === 0 ? 1 : n;
}

/** Wrap an index into [0, length): -1 is the last item, length is the first. */
export function wrapIndex(i: number, length: number): number {
  return ((i % length) + length) % length;
}
